import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { first, Subject, takeUntil } from 'rxjs';

import { ReviewReportData } from 'editing-tool/src/app/shared/components/paper/review-report/review-report-data';
import { ValidationService } from 'sc-common';
import { ApiReviewReport, ReviewRecommendationEnum } from 'sc-common/core/services/open-api/main-api-client';
import { FileUploadComponent } from 'sc-external/shared/components/file-upload/file-upload.component';

@Component({
    selector: 'et-review-report',
    templateUrl: './review-report.component.html',
    styleUrls: ['./review-report.component.scss'],
    standalone: false
})
export class ReviewReportComponent implements OnDestroy {

    public readonly reviewRecommendationEnum = ReviewRecommendationEnum;

    public readonly form: UntypedFormGroup;

    @Input()
    public set data(value: ReviewReportData) {

        this._dataRefresh$.next();

        this._data = value;
        this.form.patchValue(value.reviewReport);

        if (value.isReadonly) {
            this.form.disable();
        } else {
            this.form.enable();
        }

        if (value.retrieveData$) {

            value.retrieveData$
                .pipe(takeUntil(this._dataRefresh$), takeUntil(this._destroy$))
                .subscribe(validate => {
                    this._validationRequested = validate;

                    if (validate && this.form.invalid) {
                        this.form.markAllAsTouched();
                        return;
                    }

                    this.formValidated.emit({ validated: validate, reviewReport: this.form.value });
                });
        }
    }

    public get data(): ReviewReportData {
        return this._data;
    }

    @Output()
    public readonly formValidated = new EventEmitter<{ validated: boolean; reviewReport: ApiReviewReport; }>();

    public get isRejected(): boolean {
        return !this.data.isReadonly && this.form.get('recommendation').value === ReviewRecommendationEnum.Reject;
    }

    public get isRecommendationBlockInvalid(): boolean {

        if (!this._validationRequested || !this.form.touched) {
            return false;
        }

        return this.form.get('recommendation').invalid;
    }

    public get isChecklistBlockInvalid(): boolean {

        if (!this._validationRequested || !this.form.touched) {
            return false;
        }

        return this.form.get('highScientificQuality').invalid ||
            this.form.get('freeFromErrors').invalid ||
            this.form.get('wellOrganized').invalid ||
            this.form.get('appropriateTitle').invalid ||
            this.form.get('adequateReferences').invalid ||
            this.form.get('satisfactoryEnglish').invalid ||
            this.form.get('clearFigures').invalid ||
            this.form.get('clearTables').invalid;
    }

    public get hasRemarks(): boolean {
        return !this.data.isReadonly || this.data.isReadonly && !!this.data.reviewReport.remarks;
    }

    public get hasNoRemarks(): boolean {
        return this.data.isReadonly && !this.data.reviewReport.remarks;
    }

    public deleteAttachmentInProcess: boolean;

    @ViewChild('fileUpload')
    private readonly _fileUpload: FileUploadComponent;

    private readonly _destroy$ = new Subject<void>();

    private readonly _dataRefresh$ = new Subject<void>();

    private _data: ReviewReportData;

    private _validationRequested: boolean;

    constructor(private readonly _formBuilder: UntypedFormBuilder) {
        this.form = this._buildForm();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._dataRefresh$.complete();
    }

    public onUploadStarted(): void {

        this._fileUpload.uploadResponse$
            .pipe(first(), takeUntil(this._destroy$))
            .subscribe(file => this.data.reviewReport.attachment = file);
    }

    public onUploadCompleted(): void {
        this._fileUpload.clearFile();
    }

    public uploadDocument(): void {
        this._fileUpload.startUpload();
    }

    public deleteAttachment(): void {

        this.deleteAttachmentInProcess = true;

        this.data.deleteAttachment$
            .pipe(first(), takeUntil(this._destroy$))
            .subscribe(() => {
                this.deleteAttachmentInProcess = false;
                this.data.reviewReport.attachment = null;
            });
    }

    private _buildForm(): UntypedFormGroup {
        return this._formBuilder.group({
            recommendation: [null, [Validators.required]],
            highScientificQuality: [null, [Validators.required]],
            freeFromErrors: [null, [Validators.required]],
            wellOrganized: [null, [Validators.required]],
            appropriateTitle: [null, [Validators.required]],
            adequateReferences: [null, [Validators.required]],
            satisfactoryEnglish: [null, [Validators.required]],
            clearFigures: [null, [Validators.required]],
            clearTables: [null, [Validators.required]],
            remarks: [null, [ValidationService.alphaNumSymbolValidator]]
        });
    }
}
